package com.schoolconnect.app.feature.calendar

import com.schoolconnect.app.feature.attendance.ParentAttendanceChild
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.designsystem.currentAppDateLabel

private val CalendarMonthNames = listOf(
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
)

fun currentCalendarMonthLabel(): String {
    val parts = currentAppDateLabel().split(" ")
    val monthIndex = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
        .indexOf(parts.getOrNull(1)).coerceIn(0, 11)
    val year = parts.getOrNull(2)?.toIntOrNull() ?: 2026
    return "${CalendarMonthNames[monthIndex]} $year"
}

data class CalendarUiState(
    val isLoading: Boolean = true,
    val isSaving: Boolean = false,
    val saveGeneration: Int = 0,
    val errorMessage: String = "",
    val searchQuery: String = "",
    val month: String = currentCalendarMonthLabel(),
    val listMonthFilter: String = "",
    val eventType: String = "",
    val statusFilter: String = "",
    val listView: Boolean = true,
    val selectedId: String = "",
    val items: List<CalendarRowUi> = emptyList(),
    val totalCount: Int = 0,
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val sortField: CalendarSortField = CalendarSortField.DATE,
    val sortAscending: Boolean = true,
    val editingEvent: CalendarRowUi? = null,
    val viewingEvent: CalendarRowUi? = null,
    val deletingEvent: CalendarRowUi? = null,
    val children: List<ParentAttendanceChild> = emptyList(),
    val selectedChildIndex: Int = 0,
    val importFileName: String = "",
    val importFileBytes: ByteArray? = null,
    val importValidation: CalendarImportValidationUi? = null,
    val importIsLoading: Boolean = false,
    val importError: String = "",
    val importSuccess: String = "",
    val importCompletionGeneration: Int = 0
)

data class CalendarImportValidationUi(
    val sessionId: String,
    val fileName: String,
    val totalRows: Int,
    val validRows: Int,
    val invalidRows: Int,
    val validPayloadPreview: List<CalendarImportPreviewUi> = emptyList(),
    val errors: List<CalendarImportErrorUi> = emptyList()
)

data class CalendarImportPreviewUi(val date: String, val from: String, val to: String, val title: String, val type: String)
data class CalendarImportErrorUi(val rowNumber: String, val date: String, val title: String, val message: String)

enum class CalendarSortField { DATE, FROM, TO, TITLE, EVENT_TYPE, STATUS }

/** Values accepted by the calendar service and shared by filters and forms. */
val CalendarEventTypeOptions = listOf(
    "Academic Event", "Celebration", "Examination", "Holiday",
    "Meeting", "Other", "School Activity", "Sports"
)

val CalendarStatusOptions = listOf("DRAFT", "PUBLISHED")

data class CalendarRowUi(
    val id: String,
    val date: String = "",
    val from: String = "",
    val to: String = "",
    val title: String = "",
    val eventType: String = "",
    val status: String = "",
    val isAllDay: Boolean = false,
    val createdOn: String = "",
    val createdBy: String = "",
    val lastUpdatedOn: String = "",
    val lastUpdatedBy: String = ""
)

data class CalendarEventInputUi(
    val date: String = "",
    val allDay: Boolean = false,
    val from: String = "",
    val to: String = "",
    val title: String = "",
    val eventType: String = ""
)

sealed interface CalendarUiEvent {
    data object BackClicked : CalendarUiEvent
    data object RefreshClicked : CalendarUiEvent
    data class SearchChanged(val query: String) : CalendarUiEvent
    data class MonthChanged(val month: String) : CalendarUiEvent
    data class ListMonthChanged(val month: String) : CalendarUiEvent
    data class EventTypeChanged(val eventType: String) : CalendarUiEvent
    data class StatusChanged(val status: String) : CalendarUiEvent
    data object FiltersCleared : CalendarUiEvent
    data class AddEventsSubmitted(val events: List<CalendarEventInputUi>, val publish: Boolean) : CalendarUiEvent
    data class PublishRequested(val id: String) : CalendarUiEvent
    data class DeleteRequested(val id: String) : CalendarUiEvent
    data object DeleteDismissed : CalendarUiEvent
    data object DeleteConfirmed : CalendarUiEvent
    data class SortChanged(val field: CalendarSortField) : CalendarUiEvent
    data class PageChanged(val index: Int) : CalendarUiEvent
    data class PageSizeChanged(val size: Int) : CalendarUiEvent
    data object ListViewSelected : CalendarUiEvent
    data object CalendarViewSelected : CalendarUiEvent
    data class RowActionsToggled(val id: String) : CalendarUiEvent
    data object RowActionsDismissed : CalendarUiEvent
    data class EditRequested(val id: String) : CalendarUiEvent
    data object EditDismissed : CalendarUiEvent
    data class EditSaved(val event: CalendarRowUi) : CalendarUiEvent
    data class ViewRequested(val id: String) : CalendarUiEvent
    data object ViewDismissed : CalendarUiEvent
    data class ChildSelected(val index: Int) : CalendarUiEvent
    data class ImportFileSelected(val fileName: String, val fileBytes: ByteArray) : CalendarUiEvent
    data class ImportFileReadFailed(val message: String) : CalendarUiEvent
    data class ImportCommitRequested(val publish: Boolean) : CalendarUiEvent
    data object ImportReset : CalendarUiEvent
    data object ImportTemplateRequested : CalendarUiEvent
    data object ImportCurrentDataRequested : CalendarUiEvent
}
